import React, { useState } from 'react';
import { FileText, Mail, MessageCircle, ReceiptText, ShieldPlus, Smartphone, Truck, Undo2 } from 'lucide-react';
import { Alert, Icon, IconComponent, PageHeader, SectionHeader, Switch } from '@/components/common';
import { useAuth } from '@/context/AuthContext';
import { useDashboardQuery } from '@/hooks/useDashboardQuery';
import { formatIndianMobile } from '@/lib/support';
import type { NotificationChannel, NotificationPrefs, NotificationTopic } from '@/types/notifications';
import { DEFAULT_NOTIFICATION_PREFS } from '@/utils/notifications';

const TOPICS: { key: NotificationTopic; icon: IconComponent; title: string; description: string }[] = [
  { key: 'claims', icon: FileText, title: 'Claim updates', description: 'Status changes and requests for more details' },
  { key: 'delivery', icon: Truck, title: 'Delivery updates', description: 'Shipped, out for delivery and delivered' },
  { key: 'returns', icon: Undo2, title: 'Returns and refunds', description: 'Approval, pickup and refund status' },
  { key: 'protection', icon: ShieldPlus, title: 'Protection reminders', description: 'When a product can still be protected, and before a plan ends' },
  { key: 'plans', icon: ReceiptText, title: 'Plans and invoices', description: 'Plan confirmations and GST invoices' },
];

interface Row {
  key: string;
  icon: IconComponent;
  title: string;
  description?: string;
  checked: boolean;
  locked?: boolean;
  onChange: (on: boolean) => void;
}

const SwitchList: React.FC<{ rows: Row[]; disabled: boolean }> = ({ rows, disabled }) => (
  <ul className="tw-flex tw-flex-col tw-divide-y tw-divide-ds-border-default tw-rounded-2xl tw-border tw-border-ds-border-default tw-bg-ds-surface tw-px-5 tw-py-1">
    {rows.map((row) => (
      <li key={row.key} className="tw-flex tw-items-center tw-gap-[14px] tw-py-[14px]">
        <span className="tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-[10px] tw-bg-ds-subtle tw-text-ds-text-secondary">
          <Icon icon={row.icon} />
        </span>
        <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5">
          <span className="tw-text-[15px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary">
            {row.title}
          </span>
          {row.description && <span className="tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">{row.description}</span>}
        </span>
        <Switch checked={row.checked} onCheckedChange={row.onChange} disabled={disabled || row.locked} label={row.title} />
      </li>
    ))}
  </ul>
);

/** N4 Notification settings: which updates the customer gets, and on which channels. */
const NotificationSettingsPage: React.FC = () => {
  const { user } = useAuth();
  const phone = formatIndianMobile(user?.phone);
  // No API yet: the demo session can change these; real sessions see the defaults, read-only.
  const { data: live } = useDashboardQuery(async (demo) => Boolean(import.meta.env.DEV && demo), false, '');
  const [prefs, setPrefs] = useState<NotificationPrefs>(DEFAULT_NOTIFICATION_PREFS);

  const setTopic = (key: NotificationTopic) => (on: boolean) => setPrefs((p) => ({ ...p, topics: { ...p.topics, [key]: on } }));
  const setChannel = (key: NotificationChannel) => (on: boolean) => setPrefs((p) => ({ ...p, channels: { ...p.channels, [key]: on } }));

  const channels = [
    phone && { key: 'sms', icon: Smartphone, title: 'SMS', description: phone, checked: prefs.channels.sms, onChange: setChannel('sms') },
    phone && { key: 'whatsapp', icon: MessageCircle, title: 'WhatsApp', description: phone, checked: prefs.channels.whatsapp, onChange: setChannel('whatsapp') },
    {
      key: 'email',
      icon: Mail,
      title: 'Email',
      description: user?.email || 'No email on your account',
      checked: Boolean(user?.email) && prefs.channels.email,
      locked: !user?.email,
      onChange: setChannel('email'),
    },
  ].filter(Boolean) as Row[];

  return (
    <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-[720px] tw-flex-col tw-gap-6">
      <PageHeader
        title="Notification settings"
        subtitle={phone ? `Choose what we tell you about. Updates go by SMS and WhatsApp to ${phone}.` : 'Choose what we tell you about.'}
        back={{ to: '/customer/settings', label: 'Account' }}
      />

      {!live && <Alert title="Changing notification settings is coming soon">Soon you’ll be able to choose which updates you get, and where.</Alert>}

      <section className="tw-flex tw-flex-col tw-gap-4">
        <SectionHeader title="What we tell you about" />
        <SwitchList
          disabled={!live}
          rows={TOPICS.map((t) => ({
            ...t,
            checked: prefs.topics[t.key],
            locked: t.key === 'claims',
            onChange: setTopic(t.key),
          }))}
        />
        <Alert>Claim updates can’t be turned off — we need to reach you about claims you’ve filed.</Alert>
      </section>

      <section className="tw-flex tw-flex-col tw-gap-4">
        <SectionHeader title="How we reach you" />
        <SwitchList disabled={!live} rows={channels} />
      </section>
    </div>
  );
};

export default NotificationSettingsPage;
